import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Target, FileX, Clock, AlertCircle, Layers, TrendingUp } from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } }),
};

const scrollTo = (e, href) => { e.preventDefault(); document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }); };

export const ChallengeSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="challenge" className="py-20 lg:py-28 section-bold" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        <motion.div variants={fade} initial="hidden" animate={inView ? "visible" : "hidden"} className="text-center mb-12">
          <div className="inline-block px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 mb-4">
            <span className="text-spaced !text-[10px]" style={{ color: '#00A8B3' }}>O U R &nbsp; A P P R O A C H</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">Problem</h2>
          <p className="text-base max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Healthcare shifting to consent-driven access, but records remain dangerously fragmented.
          </p>
        </motion.div>

        {/* Problem cards — clean compact design */}
        <motion.div variants={fade} initial="hidden" animate={inView ? "visible" : "hidden"} custom={1}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-14">
            {/* Left card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3, duration: 0.6 }}
              whileHover={{ y: -6 }}
              className="w-full sm:w-72"
            >
              <div className="rounded-2xl p-6 text-white shadow-xl relative overflow-hidden"
                style={{ background: 'linear-gradient(145deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 100%)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.12)' }}>
                <motion.div className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.06) 50%, transparent 70%)' }}
                  animate={{ x: ['-100%', '200%'] }}
                  transition={{ duration: 4, repeat: Infinity, delay: 1 }} />
                <div className="flex items-center gap-3 mb-4 relative z-10">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#FF4D4F25' }}>
                    <FileX className="w-5 h-5" style={{ color: '#FF8A80' }} />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#FF8A80' }}>Pain Point</span>
                </div>
                <h3 className="text-base font-heading font-bold mb-2 relative z-10">Fragmented History</h3>
                <p className="text-sm leading-relaxed relative z-10" style={{ color: 'rgba(255,255,255,0.65)' }}>Records scattered across hospitals, labs, pharmacies with no unified view for patients or doctors.</p>
              </div>
            </motion.div>

            {/* Center alert */}
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2.5, repeat: Infinity }}
              className="flex-shrink-0 hidden sm:flex"
            >
              <div className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(255,77,79,0.15)', border: '2px solid rgba(255,77,79,0.3)' }}>
                <AlertCircle className="w-6 h-6" style={{ color: '#FF8A80' }} />
              </div>
            </motion.div>

            {/* Right card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5, duration: 0.6 }}
              whileHover={{ y: -6 }}
              className="w-full sm:w-72"
            >
              <div className="rounded-2xl p-6 text-white shadow-xl relative overflow-hidden"
                style={{ background: 'linear-gradient(145deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 100%)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.12)' }}>
                <motion.div className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.06) 50%, transparent 70%)' }}
                  animate={{ x: ['-100%', '200%'] }}
                  transition={{ duration: 4, repeat: Infinity, delay: 2.5 }} />
                <div className="flex items-center gap-3 mb-4 relative z-10">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#FF4D4F25' }}>
                    <Clock className="w-5 h-5" style={{ color: '#FF8A80' }} />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#FF8A80' }}>Pain Point</span>
                </div>
                <h3 className="text-base font-heading font-bold mb-2 relative z-10">Workflow Friction</h3>
                <p className="text-sm leading-relaxed relative z-10" style={{ color: 'rgba(255,255,255,0.65)' }}>Doctors spend minutes per consult gathering context instead of treating patients.</p>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Market stats */}
        <motion.div variants={fade} initial="hidden" animate={inView ? "visible" : "hidden"} custom={3} className="mb-12">
          <div className="glass-card-dark rounded-2xl p-8">
            <p className="text-sm leading-relaxed text-center mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              India: <strong className="text-white">85 Cr+</strong> ABHA IDs, <strong className="text-white">2.5B+</strong> OPD visits,
              <strong className="text-white"> $18B+</strong> digital health market (20% CAGR to <strong className="text-white">$100B by 2033</strong>).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[{v:"85 Cr+",l:"ABHA IDs Created"},{v:"2.5B+",l:"OPD Visits / Year"},{v:"$100B",l:"Market by 2033"}].map(s=>(
                <div key={s.v} className="text-center">
                  <p className="text-2xl lg:text-3xl font-heading font-bold text-gradient-teal">{s.v}</p>
                  <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.45)' }}>{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div variants={fade} initial="hidden" animate={inView ? "visible" : "hidden"} custom={5} className="text-center">
          <Button size="lg" className="btn-teal px-8" onClick={(e) => scrollTo(e, "#demo")}>Book a Demo</Button>
        </motion.div>
      </div>
    </section>
  );
};
